import { useEffect, useRef, useState } from 'react';
import type { DemoKey, DemoScript, FingerKey, Fx, Level, Pt, RoundProps, ViewProps } from '../kit.js';
import { BAD, BOARD, BoardFrame, OK, STROKE, TYPE, alpha, clamp, easier, mono, q, say, seeded, thirtyGame, useRound, useSvgId } from '../kit.js';
import { ERA_SKIN } from '../../art.js';
import { SEQ_IDS, STAGES, StagePicture } from './stages.js';
import type { SeqId } from './stages.js';
import { paperOf, pick, pop, shuffle } from './things.js';

/**
 * ARCHIVIST · IN ORDER — tap the pictures in the order they happened.
 *
 * Archive cards of something everyday in its stages (a seed becoming a tree, a candle burning
 * down, a glass being filled…), shuffled. Tap the one that came first, then the next; each slots
 * into a numbered row under them. A card out of turn is a mistake (it is ringed in red, and stays
 * where it is). Two sets a round: three cards each in rounds 1 and 2 (first, middle, last — far
 * apart), four in round 3.
 *
 * On the kit (kit.tsx): the cards and the row are drawn in the board's own 320 × 320 space; the
 * view state (`OrderView`) holds the cards as they lie and how far each has gone into the row.
 */

/** Easier since 6 Oct 2026 (kit `easier`): round 1 as before, round 2 halfway to the old round 2, round 3 the old round 2. */
export const ORDER_LEVELS: readonly { cards: number; sets: number }[] = easier([
  { cards: 3, sets: 2 },
  { cards: 4, sets: 2 },
]);

/** Which of the five stages a set shows: far apart with three cards, all five at the end. */
export function stagesFor(cards: number, rand: () => number): number[] {
  if (cards >= STAGES) return Array.from({ length: STAGES }, (_, i) => i);
  if (cards === 3) return [0, 2, 4];
  const drop = 1 + Math.floor(rand() * (STAGES - 2));
  return Array.from({ length: STAGES }, (_, i) => i).filter((i) => i !== drop);
}

export interface OrderSet {
  seq: SeqId;
  /** The stages, in the order they happened. */
  stages: number[];
  /** The same stages as the cards lie, never already in order. */
  pool: number[];
}

/** The sequences used lately, so the rounds show different ones. */
const recent: SeqId[] = [];

/** A round's sets. */
export function orderDeal(level: Level, rand: () => number, avoid: readonly SeqId[] = []): OrderSet[] {
  const { cards, sets } = ORDER_LEVELS[level]!;
  const fresh = SEQ_IDS.filter((s) => !avoid.includes(s));
  const seqs = pick(fresh.length >= sets ? fresh : SEQ_IDS, sets, rand);
  return seqs.map((seq) => {
    const stages = stagesFor(cards, rand);
    let pool = shuffle(stages, rand);
    while (pool.every((s, i) => s === stages[i])) pool = shuffle(stages, rand);
    return { seq, stages, pool };
  });
}

/** After a set is in order, the next set comes this much later. */
const SET_DONE_MS = 900;
/** A card lifts off into the row over this. */
const LIFT_MS = 250;
/** It lands in its place over this. */
const LAND_MS = 280;
/** A card tapped out of turn is ringed in red this long. */
const WRONG_MS = 700;

/** The cards lie in the top part of the board; the numbered row runs along the bottom (board units). */
const POOL = { top: 12, h: 194, gap: 8, w: 96 } as const;
const ROW = { x: 10, y: 222, w: BOARD.w - 20, h: 88, slotH: 74, gap: 6 } as const;

/** Where card `i` of `n` lies: up to three in a row, more in two rows (four as 2 + 2, five as 3 + 2). */
export function cardBox(i: number, n: number): { x: number; y: number; w: number; h: number } {
  const rows = n <= 3 ? 1 : 2;
  const first = rows === 1 ? n : Math.ceil(n / 2);
  const row = i < first ? 0 : 1;
  const inRow = row === 0 ? first : n - first;
  const k = row === 0 ? i : i - first;
  const h = rows === 1 ? 124 : (POOL.h - POOL.gap) / 2;
  const left = (BOARD.w - inRow * POOL.w - (inRow - 1) * POOL.gap) / 2;
  const top = POOL.top + (POOL.h - (rows * h + (rows - 1) * POOL.gap)) / 2;
  return { x: left + k * (POOL.w + POOL.gap), y: top + row * (h + POOL.gap), w: POOL.w, h };
}

/** The card under a finger at `p`, or −1. */
export function cardAt(p: Pt, n: number): number {
  for (let i = 0; i < n; i++) {
    const b = cardBox(i, n);
    if (p.x >= b.x - POOL.gap / 2 && p.x <= b.x + b.w + POOL.gap / 2 && p.y >= b.y - POOL.gap / 2 && p.y <= b.y + b.h + POOL.gap / 2) return i;
  }
  return -1;
}

/** Place `k` of `n` in the numbered row. */
export function slotBox(k: number, n: number): { x: number; y: number; w: number; h: number } {
  const w = Math.min(60, Math.floor((ROW.w - 12 - (n - 1) * ROW.gap) / n));
  const left = (BOARD.w - n * w - (n - 1) * ROW.gap) / 2;
  return { x: left + k * (w + ROW.gap), y: ROW.y + (ROW.h - ROW.slotH) / 2, w, h: ROW.slotH };
}

/** Everything the cards and the row show. */
export interface OrderView {
  /** The game clock, ms. */
  t: number;
  /** The sequence. */
  q: SeqId;
  /** Its stages in the order they happened: the numbered row. */
  st: readonly number[];
  /** The cards as they lie: each one's stage. */
  c: readonly number[];
  /** Each card lifted off into the row, 0–1. */
  g: readonly number[];
  /** Each place in the row filled, 0–1. */
  sl: readonly number[];
  /** A card tapped out of turn, and how long ago (ms), until the next right one; else null. */
  w: readonly [i: number, age: number] | null;
  /** Which set this is, and how many the round has. */
  a: number;
  of: number;
  /** 0 placing, 1 this set is in order, 2 every set in order. */
  s: 0 | 1 | 2;
  fx?: Fx | undefined;
}

/** The cards and the row, from the view state alone. */
function View({ view, era, accent, lang, input }: ViewProps<OrderView>) {
  const id = useSvgId();
  const skin = ERA_SKIN[era];
  const paper = paperOf(era);
  const folder = era === 'debris' || era === 'dust';
  const n = view.st.length;
  // A demo glides every number between its keys: a place counts as filled as soon as its card is on the way.
  const placed = view.sl.filter((v) => v > 0).length;
  const done = view.s > 0;
  const status = done
    ? { text: say(lang, '✓ IN ORDER', '✓ I REKKEFØLGE'), tone: 'ok' as const }
    : view.w
      ? { text: say(lang, 'NOT YET', 'IKKE ENNÅ'), tone: 'bad' as const }
      : {
          text: placed === 0 ? say(lang, 'WHAT CAME FIRST?', 'HVA KOM FØRST?') : say(lang, 'AND THEN?', 'OG SÅ?'),
          hint: view.of > 1 ? say(lang, `SET ${Math.round(view.a) + 1} OF ${view.of}`, `SETT ${Math.round(view.a) + 1} AV ${view.of}`) : undefined,
        };
  const wrong = view.w && view.w[1] < WRONG_MS ? view.w : null;

  return (
    <BoardFrame era={era} accent={accent} status={status} fx={view.fx} input={input}>
      <defs>
        <linearGradient id={`${id}card`} x1="0" y1="0" x2=".3" y2="1">
          <stop offset="0" stopColor={folder ? '#f2dca8' : paper.top} />
          <stop offset="1" stopColor={folder ? '#d9b878' : paper.bottom} />
        </linearGradient>
        <linearGradient id={`${id}paper`} x1="0" y1="0" x2=".3" y2="1">
          <stop offset="0" stopColor={paper.top} />
          <stop offset="1" stopColor={paper.bottom} />
        </linearGradient>
      </defs>

      {/* the cards as they lie; a card placed lifts off and fades */}
      {view.c.map((stage, i) => {
        const b = cardBox(i, view.c.length);
        const gone = clamp(view.g[i] ?? 0, 0, 1);
        if (gone >= 1) return null;
        const s = 1 - 0.15 * gone;
        const size = Math.min(84, b.w - 10, b.h - 10);
        const ringed = wrong && Math.round(wrong[0]) === i;
        return (
          <g
            key={i}
            opacity={q(1 - gone, 2)}
            transform={`translate(${q(b.x + b.w / 2, 1)} ${q(b.y + b.h / 2 + 16 * gone, 1)}) scale(${q(s, 3)}) translate(${-b.w / 2} ${-b.h / 2})`}
          >
            <rect x="1" y="4" width={b.w} height={b.h} rx="9" fill="#000000" opacity=".5" />
            {folder && <path d="M8 1V-6Q8 -9 11 -9H39Q42 -9 42 -6V1Z" fill="#e8cf94" stroke={paper.edge} strokeWidth={STROKE.line} />}
            <rect width={b.w} height={b.h} rx="9" fill={`url(#${id}card)`} stroke={ringed ? BAD : paper.edge} strokeWidth={ringed ? STROKE.bold : STROKE.line} />
            <rect x="5" y="5" width={b.w - 10} height={b.h - 10} rx="5" fill="#ffffff" opacity=".55" />
            <StagePicture seq={view.q} stage={Math.round(stage)} x={b.w / 2 - size / 2} y={b.h / 2 - size / 2} size={size} />
          </g>
        );
      })}

      {/* the numbered row they go into */}
      <rect x={ROW.x} y={ROW.y} width={ROW.w} height={ROW.h} rx="10" fill="#000000" opacity=".28" />
      <rect x={ROW.x} y={ROW.y} width={ROW.w} height={ROW.h} rx="10" fill="none" stroke={alpha(skin.ink, 0.2)} />
      {view.st.map((stage, k) => {
        const b = slotBox(k, n);
        const fill = clamp(view.sl[k] ?? 0, 0, 1);
        const land = pop(fill);
        const size = Math.min(b.w - 6, b.h - 21);
        return (
          <g key={k}>
            {done && <rect x={b.x - 2} y={b.y - 2} width={b.w + 4} height={b.h + 4} rx="9" fill="none" stroke={OK} strokeWidth="4" opacity=".35" />}
            {fill > 0 ? (
              <g opacity={q(land.opacity, 2)}>
                <rect x={b.x} y={b.y} width={b.w} height={b.h} rx="7" fill={`url(#${id}paper)`} stroke={done ? OK : paper.edge} strokeWidth={STROKE.line} />
                <g transform={`translate(${q(b.x + b.w / 2, 1)} ${q(b.y + 18 + size / 2, 1)}) scale(${q(land.scale, 3)})`}>
                  <StagePicture seq={view.q} stage={Math.round(stage)} x={-size / 2} y={-size / 2} size={size} />
                </g>
              </g>
            ) : (
              <rect x={b.x} y={b.y} width={b.w} height={b.h} rx="7" fill="none" stroke={alpha(skin.ink, 0.47)} strokeWidth="1.5" strokeDasharray="4 3" />
            )}
            <text x={b.x + b.w / 2} y={b.y + 14} textAnchor="middle" fontFamily={mono} fontSize={TYPE.hint} fontWeight="700" fill={fill > 0 ? '#2a2118' : alpha(skin.ink, 0.8)}>
              {k + 1}
            </text>
          </g>
        );
      })}
    </BoardFrame>
  );
}

function Board(props: RoundProps) {
  const { level, era, lang } = props;
  const [sets] = useState(() => orderDeal(level, seeded(Date.now() + level * 7919), recent.slice(0, 4)));
  useEffect(() => {
    for (const { seq } of sets) {
      if (recent.includes(seq)) recent.splice(recent.indexOf(seq), 1);
      recent.unshift(seq);
    }
    recent.length = Math.min(recent.length, 6);
  }, [sets]);
  const at = useRef(0);
  /** When each place in the row was filled (game clock), in order. */
  const placedAt = useRef<number[]>([]);
  /** The last card tapped out of turn, and when. */
  const wrong = useRef<{ i: number; at: number } | null>(null);
  /** When the next set comes. */
  const due = useRef<number | null>(null);

  const round = useRound(props, (_dt, now) => {
    if (due.current === null || now < due.current) return;
    due.current = null;
    at.current += 1;
    placedAt.current = [];
    wrong.current = null;
  });

  const tap = (p: Pt) => {
    if (!round.live || due.current !== null) return;
    const set = sets[at.current]!;
    const i = cardAt(p, set.pool.length);
    if (i < 0) return;
    const stage = set.pool[i]!;
    const placed = placedAt.current.length;
    // A card already in the row is no longer there to tap.
    if (set.stages.indexOf(stage) < placed) return;
    const now = round.time();
    if (stage === set.stages[placed]) {
      placedAt.current = [...placedAt.current, now];
      wrong.current = null;
      round.hit();
      round.progress((at.current + (placed + 1) / set.stages.length) / sets.length);
      if (placed + 1 >= set.stages.length) {
        if (at.current + 1 >= sets.length) round.win();
        else due.current = now + SET_DONE_MS;
      }
      return;
    }
    wrong.current = { i, at: now };
    round.miss();
  };

  const now = round.now;
  const set = sets[at.current]!;
  const placed = placedAt.current;
  const view: OrderView = {
    t: q(now),
    q: set.seq,
    st: set.stages,
    c: set.pool,
    g: set.pool.map((stage) => {
      const k = set.stages.indexOf(stage);
      return k < placed.length ? q(clamp((now - placed[k]!) / LIFT_MS, 0, 1), 2) : 0;
    }),
    sl: set.stages.map((_, k) => (k < placed.length ? q(clamp((now - placed[k]!) / LAND_MS, 0, 1), 2) : 0)),
    w: wrong.current ? [wrong.current.i, q(Math.min(9_999, now - wrong.current.at))] : null,
    a: at.current,
    of: sets.length,
    s: round.won ? 2 : placed.length >= set.stages.length ? 1 : 0,
    fx: round.fx,
  };
  return (
    <View
      view={round.show(view)}
      era={era}
      accent={props.accent}
      lang={lang}
      input={{ label: say(lang, 'Tap the pictures in the order they happened', 'Trykk på bildene i den rekkefølgen det skjedde'), down: tap }}
    />
  );
}

/**
 * The how-to, on round 1: three candles, shuffled; the finger taps the tall one, the half one and
 * the stub, and each drops into its place in the row. Then the next set comes, a seed growing.
 */
function demoSets(): DemoScript<OrderView> {
  const mid = (i: number) => {
    const b = cardBox(i, 3);
    return { x: q(b.x + b.w / 2), y: q(b.y + b.h / 2) };
  };
  const g = [0, 0, 0];
  const sl = [0, 0, 0];
  const keys: DemoKey<OrderView>[] = [
    { at: 0, view: { t: 0, q: 'candle', st: [0, 2, 4], c: [4, 0, 2], g: [...g], sl: [...sl], w: null, a: 0, of: 2, s: 0 } },
  ];
  const finger: FingerKey[] = [
    { at: 0, x: 280, y: 300, hide: true },
    { at: 400, x: 280, y: 300, hide: false },
  ];
  /** The finger taps card `i` at `at`, and it goes to place `k`. */
  const place = (i: number, k: number, at: number, last = false) => {
    const c = mid(i);
    finger.push({ at: at - 400, x: c.x, y: c.y }, { at, x: c.x, y: c.y, down: true }, { at: at + 150, x: c.x, y: c.y, down: false });
    keys.push({ at, view: { g: [...g], sl: [...sl], fx: [0.8, 0], ...(last ? { s: 1 as const } : {}) }, ease: 'step' });
    g[i] = 1;
    sl[k] = 1;
    keys.push({ at: at + LAND_MS, view: { g: [...g], sl: [...sl] } });
    keys.push({ at: at + 450, view: { fx: [0, 0] } });
  };
  place(1, 0, 1_200);
  place(2, 1, 2_200);
  place(0, 2, 3_200, true);
  // The next set: a seed growing, shuffled.
  g.fill(0);
  sl.fill(0);
  keys.push({ at: 4_400, view: { q: 'grow', c: [2, 4, 0], g: [...g], sl: [...sl], a: 1, s: 0 }, ease: 'step' });
  finger.push({ at: 3_800, x: 280, y: 300 });
  place(2, 0, 5_400);
  finger.push({ at: 6_200, x: 280, y: 300 }, { at: 6_400, x: 280, y: 300, hide: true });
  return {
    ms: 7_200,
    keys,
    finger,
    steps: [
      { at: 0, en: 'TAP WHAT CAME FIRST', no: 'TRYKK PÅ DET SOM KOM FØRST' },
      { at: 1_500, en: 'THEN THE NEXT ONE', no: 'SÅ DET NESTE' },
      { at: 4_200, en: 'TWO SETS A ROUND', no: 'TO SETT HVER RUNDE' },
    ],
  };
}

export const game = thirtyGame<OrderView>({
  Board,
  View,
  demo: demoSets(),
  words: {
    en: {
      instruction: 'Tap the pictures in the order they happened: first, then next, then last.',
      howMove: 'Tap the picture that came first, then the next one, and so on.',
    },
    no: {
      instruction: 'Trykk på bildene i den rekkefølgen det skjedde: først, så neste, til sist.',
      howMove: 'Trykk på bildet som kom først, så det neste, og så videre.',
    },
  },
});
